import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FolderTree } from 'lucide-react';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useGroups } from '@/hooks/useGroups';
import { formatRelativeDate } from '@/lib/formatters';
import type { PrincipalGroup } from '@/api/groups';

const PER_PAGE = 25;

interface GroupsTabProps {
  /** Clicking a group jumps to the Users tab filtered by that group. */
  onSelectGroup: (groupName: string) => void;
}

/** Groups tab — every IdP-synced or SCIM-pushed group with live member counts. */
export function GroupsTab({ onSelectGroup }: GroupsTabProps) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => setPage(1), [search]);

  const { data, isLoading } = useGroups({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
  });
  const rows = data?.data ?? [];
  const pagination = data?.meta.pagination;

  const columns: Column<PrincipalGroup>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (g) => <span className="font-medium text-foreground">{g.name}</span>,
    },
    {
      key: 'source',
      header: 'Source',
      render: (g) => g.identity_provider_name || '—',
    },
    {
      key: 'members',
      header: 'Members',
      render: (g) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="tabular-nums">{g.member_count}</span>
            </TooltipTrigger>
            <TooltipContent>Active members only</TooltipContent>
          </Tooltip>
      ),
    },
    {
      key: 'updated',
      header: 'Updated',
      render: (g) => (g.updated_at ? formatRelativeDate(g.updated_at) : '—'),
    },
  ];

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search by group name…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="max-w-xs"
      />

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length === 0 && !search ? (
        <EmptyState
          icon={<FolderTree className="h-12 w-12" />}
          title="No groups yet"
          description="Groups appear here after an identity provider sync or a SCIM Push Groups operation from your IdP."
          action={
            <Button asChild>
              <Link to="/identity-providers">Connect an identity provider</Link>
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          keyExtractor={(g) => g.id}
          onRowClick={(g) => onSelectGroup(g.name)}
          emptyMessage="No groups match the current search."
        />
      )}

      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {rows.length} of {pagination.total} · page {pagination.page} of{' '}
            {pagination.total_pages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
